import React, { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEO from "@/components/SEO";
import RichContent from "@/components/RichContent";
import publicApi from "@/lib/publicApi";
import usePageContent, { s } from "@/hooks/usePageContent";
import { parseLocaleFromPath } from "@/lib/locale";

const CATEGORIES = ["all", "workshop", "instruments", "restoration"];

export default function Gallery() {
  const { t } = useTranslation();
  const { locale } = parseLocaleFromPath(useLocation().pathname);
  const { data: page } = usePageContent("gallery", locale);
  const [images, setImages] = useState([]);
  const [active, setActive] = useState("all");

  useEffect(() => {
    publicApi.get("/public/gallery", { params: { locale } }).then((r) => setImages(r.data)).catch(() => setImages([]));
  }, [locale]);

  const filtered = useMemo(
    () => (active === "all" ? images : images.filter((i) => i.category === active)),
    [images, active]
  );

  return (
    <>
      <SEO title={page?.meta_title} description={page?.meta_description} />
      <section className="bg-background grain-bg">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.3em] text-primary mb-4">{s(page, "eyebrow")}</div>
            <h1 className="font-serif text-5xl lg:text-6xl text-foreground leading-[1.05]">{s(page, "title")}</h1>
            <p className="mt-5 text-lg text-foreground/75">{s(page, "subtitle")}</p>
          </div>

          <div className="mt-10 flex flex-wrap gap-2" data-testid="gallery-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                data-testid={`gallery-filter-${c}`}
                onClick={() => setActive(c)}
                className={`px-4 py-2 rounded-full text-sm transition-colors ${
                  active === c
                    ? "bg-primary text-primary-foreground shadow-interactive"
                    : "border border-border text-foreground/75 hover:text-primary hover:border-primary/50"
                }`}
              >
                {s(page, `filter_${c}`) || t(`pages:gallery.filter_${c}`)}
              </button>
            ))}
          </div>

          <div
            className="mt-10 columns-1 sm:columns-2 lg:columns-3 gap-5 space-y-5"
            data-testid="gallery-grid"
          >
            {filtered.map((img) => (
              <figure
                key={img.id}
                data-testid={`gallery-item-${img.id}`}
                className="break-inside-avoid rounded-lg overflow-hidden border border-border/60 shadow-ambient bg-card group"
              >
                <img
                  src={img.url}
                  alt={img.caption}
                  loading="lazy"
                  className="w-full h-auto object-cover group-hover:scale-[1.03] transition-transform duration-700"
                />
                <figcaption className="px-4 py-3 text-xs text-muted-foreground italic">{img.caption}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
